<template>
  <div>
    <!-- BARRE D'ONGLETS DU BAS - mobile uniquement (cf. media query plus bas).
         Elle remplace les deux tiroirs qui coexistaient : celui de l'aside et celui
         des menus de premier niveau, chacun ouvert par son bouton dans le header.
         Deux tiroirs pour une meme navigation, c'est une hesitation, pas un choix. -->
    <nav class="mtb" role="navigation" aria-label="Navigation principale">
      <div class="mtb-scroll">
        <button v-for="menu in tabs" :key="'tab_' + menu.name" type="button"
                class="mtb-tab" :class="{active: isMenuSelected(menu)}"
                @click="onTab(menu)">
          <span class="mtb-tab-ico">
            <i :class="menu.icon || 'fad fa-circle'"></i>
            <span v-if="badgeForMenu(menu)" class="mtb-dot">{{ badgeForMenu(menu) }}</span>
          </span>
          <span class="mtb-tab-label">{{ menu.label ? trans(menu.label) : menu.name }}</span>
        </button>
      </div>
    </nav>

    <!-- Sous-menu de la section, au-dessus de la barre. Le voile passe EN DESSOUS
         d'elle : on peut donc passer d'une section a l'autre sans fermer, et sans
         calculer une hauteur a la main. -->
    <transition name="mtb-pop">
      <div v-if="popOpen" class="mtb-pop-overlay" @click.self="closePop()">
        <div class="mtb-pop">
          <div class="mtb-pop-head">
            <span>{{ popTitle }}</span>
            <button type="button" class="mtb-pop-close" @click="closePop()">
              <i class="fad fa-xmark"></i>
            </button>
          </div>
          <div class="mtb-pop-grid">
            <template v-for="sub in popItems">
              <!-- Lien externe : le menu servi par auth peut en contenir. Les filtrer
                   ferait disparaitre une entree sans que rien ne le dise. -->
              <a v-if="sub.href" :key="'ph_' + sub.href" :href="sub.href" target="_blank"
                 class="mtb-card" @click="closePop()">
                <span class="mtb-card-ico">
                  <i :class="sub.icon || 'fad fa-arrow-up-right-from-square'"></i>
                </span>
                <span class="mtb-card-label">{{ sub.label ? trans(sub.label) : sub.href }}</span>
              </a>
              <router-link v-else :key="'pl_' + sub.name"
                           :to="{name: sub.name, query: subQuery(sub)}"
                           class="mtb-card" :class="{active: isSubActive(sub)}"
                           @click.native="closePop()">
                <span class="mtb-card-ico">
                  <i :class="sub.icon || 'fad fa-circle'"></i>
                  <span v-if="badgeFor(sub.name)" class="mtb-dot">{{ badgeFor(sub.name) }}</span>
                </span>
                <span class="mtb-card-label">{{ sub.label ? trans(sub.label) : sub.name }}</span>
              </router-link>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
/* eslint-disable  @typescript-eslint/no-explicit-any */
import {Component, Vue, Watch} from 'vue-property-decorator'
import {auth} from '@/services/Auth'
import store from '@/store'
import {helper} from '@/services/Helper'

@Component
export default class MobileTabBar extends Vue {
  popOpen = false
  popTitle = ''
  popItems: any[] = []

  /** Menus de premier niveau autorises : meme source et meme filtre que l'aside. */
  get tabs(): any[] {
    const menus = store.state.mainStore.menus || []
    return menus.filter((m: any) => m && this.isMenuAllowed(m))
  }

  isMenuAllowed(menu: any): boolean {
    return auth.checkRoleTabs(menu.roles)
  }

  /**
   * Section active : comparaison sur la RACINE du chemin, pas sur le nom de route.
   * Une page de detail (/income/contract/show/xxx) doit garder sa section allumee.
   */
  isMenuSelected(menu: any): boolean {
    if (!menu || !menu.path) return false
    const root = menu.path.replace(/^\//, '').split('/')[0]
    const currentRoot = this.$route.path.replace(/^\//, '').split('/')[0]
    return !!root && currentRoot === root
  }

  isSubActive(sub: any): boolean {
    return this.$route.name === sub.name
  }

  allowedSubMenus(menu: any): any[] {
    const subs = menu && Array.isArray(menu.subMenus) ? menu.subMenus : []
    return subs.filter((s: any) => s && (s.name || s.href) && this.isMenuAllowed(s))
  }

  /**
   * Un onglet qui porte des sous-menus OUVRE la section, il n'y navigue pas :
   * atterrir sur la premiere page obligerait a en ressortir pour choisir.
   * Un onglet sans sous-menu autorise navigue directement.
   */
  onTab(menu: any) {
    const subs = this.allowedSubMenus(menu)
    if (subs.length > 0) {
      this.popTitle = menu.label ? (this as any).trans(menu.label) : menu.name
      this.popItems = subs
      this.popOpen = true
      return
    }
    if (menu.name && this.$route.name !== menu.name) {
      this.$router.push({name: menu.name})
    }
  }

  closePop() {
    this.popOpen = false
  }

  /** Un changement de page ferme le sous-menu, retour arriere du navigateur compris. */
  @Watch('$route')
  onRouteChange() {
    this.closePop()
  }

  subQuery(sub: any): any {
    if (!sub.params || sub.params.length === 0) return undefined
    const query: any = {}
    for (const p of sub.params) {
      if (p && p.name) query[p.name] = p.value
    }
    return helper.empty(query) ? undefined : query
  }

  /**
   * Meme compteur que l'aside : sans lui, un agent passe sur mobile perdrait le
   * signal des escalades en attente.
   */
  badgeFor(menuName: string): number {
    if (menuName !== 'chat-escalations') return 0
    const notifs = store.state.mainStore.notifications || []
    return notifs.filter((n: any) => n && n.type === 'ESCALATION').length
  }

  /** Compteur d'une section = somme de ses sous-menus, sinon il reste invisible. */
  badgeForMenu(menu: any): number {
    return this.allowedSubMenus(menu)
        .reduce((total: number, sub: any) => total + this.badgeFor(sub.name), 0)
  }
}
</script>

<style scoped>
/* Masquee par defaut : c'est la media query qui l'allume. */
.mtb {
  display: none;
}

.mtb-dot {
  position: absolute;
  top: -4px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #f1416c;
  color: white;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

@media (max-width: 991.98px) {
  .mtb {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    /* Sous les modales de l'application (>= 1050) : elles ont souvent leurs boutons
       en bas, la barre les masquerait. */
    z-index: 1020;
    display: block;
    background: #ffffff;
    border-top: 1px solid #e4e6ef;
    box-shadow: 0 -6px 20px -10px rgba(24, 28, 50, 0.18);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .mtb-scroll {
    display: flex;
    align-items: stretch;
    gap: 2px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 6px 8px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .mtb-scroll::-webkit-scrollbar {
    display: none;
  }

  /* Les onglets debordent et defilent plutot que de se comprimer : le nombre de
     sections depend des roles, une largeur fixe finirait par les rendre illisibles. */
  .mtb-tab {
    flex: 1 0 auto;
    min-width: 66px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    padding: 6px 12px;
    border-radius: 10px;
    color: #7e8299;
    cursor: pointer;
    transition: color 0.14s ease, background 0.14s ease;
  }

  .mtb-tab-ico {
    position: relative;
    font-size: 20px;
    line-height: 24px;
  }

  .mtb-tab-label {
    font-size: 11px;
    font-weight: 600;
    line-height: 1.15;
    text-align: center;
    white-space: nowrap;
  }

  .mtb-tab.active {
    color: #00cdd5;
    background: #ecf8ff;
  }
}

/* --- Sous-menu de section --- */
.mtb-pop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* Juste SOUS la barre : elle reste visible et cliquable par-dessus le voile. */
  z-index: 1019;
  background: rgba(24, 28, 50, 0.4);
}

.mtb-pop {
  position: fixed;
  left: 8px;
  right: 8px;
  bottom: calc(70px + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  max-height: 62vh;
  background: white;
  border: 1px solid #e4e6ef;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(24, 28, 50, 0.25);
  overflow: hidden;
}

.mtb-pop-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 16px 11px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7e8299;
  border-bottom: 1px solid #f1f3f8;
}

.mtb-pop-close {
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: #f1f3f8;
  color: #5e6278;
  cursor: pointer;
}

.mtb-pop-grid {
  overflow-y: auto;
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.mtb-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 10px 6px;
  height: 104px;
  border-radius: 12px;
  background: #f9fafc;
  border: 1px solid #e4e6ef;
  text-decoration: none;
  color: #5e6278;
  text-align: center;
  transition: border-color 0.14s ease, background 0.14s ease, color 0.14s ease;
}

/* Ni cadre ni fond : l'icone se suffit. La pastille ajoutait un troisieme
   niveau de bordure (carte + pastille + carte active) pour rien. */
.mtb-card-ico {
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #5e6278;
  font-size: 22px;
  line-height: 1;
}

.mtb-card-label {
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.2;
  color: #181c32;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mtb-card:active {
  background: #f1f3f8;
}

.mtb-card.active {
  border-color: #00cdd5;
  background: #ecf8ff;
}

.mtb-card.active .mtb-card-ico {
  color: #00cdd5;
}

.mtb-pop-enter-active,
.mtb-pop-leave-active {
  transition: opacity 0.18s ease;
}

.mtb-pop-enter-active .mtb-pop,
.mtb-pop-leave-active .mtb-pop {
  transition: transform 0.22s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.22s ease;
}

.mtb-pop-enter,
.mtb-pop-leave-to {
  opacity: 0;
}

.mtb-pop-enter .mtb-pop,
.mtb-pop-leave-to .mtb-pop {
  transform: translateY(12px);
  opacity: 0;
}
</style>
